import { PayloadAction } from '@reduxjs/toolkit'
import {
  INewGeneratedItemsInitialIdAndStatus,
  ITtsState
} from 'store/types/tts'
import { ITtsItem, TTtsGenerationStatus } from 'types/text-to-speech'
import {
  bulkUpdateTtsValuesByKey,
  removeDraftEmptyItems,
  setTtsItemStateByKey,
  setTtsItemValueByKey
} from './helpers'

const reducers = {
  setInitialTtsItems: (state: ITtsState, action: PayloadAction<ITtsItem[]>) => {
    state.ttsInitialItems = {
      data: action.payload,
      isLoading: false
    }
  },
  setInitialTtsItemsIsLoading: (
    state: ITtsState,
    action: PayloadAction<boolean>
  ) => {
    state.ttsInitialItems.isLoading = action.payload
  },
  setTtsItems: (state: ITtsState, action: PayloadAction<ITtsItem[]>) => {
    state.ttsMutableItems = {
      data: action.payload,
      isLoading: false
    }
  },
  setTtsItemsIsLoading: (state: ITtsState, action: PayloadAction<boolean>) => {
    state.ttsMutableItems.isLoading = action.payload
  },
  addNewTtsBlock: (state: ITtsState, action: PayloadAction<ITtsItem>) => {
    state.ttsMutableItems.data.push(action.payload)
  },
  setTtsCredits: (state: ITtsState, action: PayloadAction<number>) => {
    state.ttsCredits = {
      data: action.payload,
      isLoading: false
    }
  },
  setTtsCreditsIsLoading: (
    state: ITtsState,
    action: PayloadAction<boolean>
  ) => {
    state.ttsCredits.isLoading = action.payload
  },
  updateTtsItemDataText: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: string
    }>
  ) => {
    const { id, value } = action.payload
    setTtsItemValueByKey({
      mutableTtsItems: state.ttsMutableItems.data,
      key: 'text',
      id,
      value
    })
  },
  updateTtsItemDataSpeakerId: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: number
    }>
  ) => {
    const { id, value } = action.payload
    setTtsItemValueByKey({
      mutableTtsItems: state.ttsMutableItems.data,
      key: 'speakerId',
      id,
      value
    })
  },
  updateTtsItemDataSpeed: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: number
    }>
  ) => {
    const { id, value } = action.payload
    setTtsItemValueByKey({
      mutableTtsItems: state.ttsMutableItems.data,
      key: 'speed',
      id,
      value
    })
  },
  updateTtsItemDataLangCode: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: string
    }>
  ) => {
    const { id, value } = action.payload
    setTtsItemValueByKey({
      mutableTtsItems: state.ttsMutableItems.data,
      key: 'langCode',
      id,
      value
    })
  },
  updateTtsItemDataInstruction: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: string
    }>
  ) => {
    const { id, value } = action.payload
    setTtsItemValueByKey({
      mutableTtsItems: state.ttsMutableItems.data,
      key: 'instructions',
      id,
      value
    })
  },
  bulkUpdateTtsItemsLangCode: (
    state: ITtsState,
    action: PayloadAction<{
      unchangedItems: number[]
      value: string
    }>
  ) => {
    const { unchangedItems, value } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems

    bulkUpdateTtsValuesByKey({
      mutableTtsItems,
      key: 'langCode',
      value,
      unchangedItems
    })
  },
  bulkUpdateTtsItemsSpeakerId: (
    state: ITtsState,
    action: PayloadAction<{
      unchangedItems: number[]
      value: number
    }>
  ) => {
    const { unchangedItems, value } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems

    bulkUpdateTtsValuesByKey({
      mutableTtsItems,
      key: 'speakerId',
      value,
      unchangedItems
    })
  },
  bulkUpdateTtsItemsSpeed: (
    state: ITtsState,
    action: PayloadAction<{
      unchangedItems: number[]
      value: number
    }>
  ) => {
    const { unchangedItems, value } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems

    bulkUpdateTtsValuesByKey({
      mutableTtsItems,
      key: 'speed',
      value,
      unchangedItems
    })
  },
  bulkUpdateTtsItemsInstruction: (
    state: ITtsState,
    action: PayloadAction<{
      unchangedItems: number[]
      value: string
    }>
  ) => {
    const { unchangedItems, value } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems

    bulkUpdateTtsValuesByKey({
      mutableTtsItems,
      key: 'instructions',
      value,
      unchangedItems
    })
  },
  updateTtsGeneratedState: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: boolean
    }>
  ) => {
    const { id, value } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems

    setTtsItemStateByKey({
      mutableTtsItems,
      stateKey: 'isGenerated',
      id,
      value
    })
  },
  updateTtsLoadingState: (
    state: ITtsState,
    action: PayloadAction<{
      id: number
      value: boolean
    }>
  ) => {
    const { id, value } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems

    setTtsItemStateByKey({
      mutableTtsItems,
      stateKey: 'isLoading',
      id,
      value
    })
  },
  deleteDraftTtsItem: (
    state: ITtsState,
    action: PayloadAction<{ id: number }>
  ) => {
    const { id } = action.payload
    const { data: mutableTtsItems } = state.ttsMutableItems
    const itemIndex = mutableTtsItems.findIndex(item => item.data.id === id)

    if (itemIndex !== -1) {
      mutableTtsItems.splice(itemIndex, 1)
    }
  },
  deleteAllDraftEmptyTtsItems: (state: ITtsState) => {
    const { data: mutableTtsItems } = state.ttsMutableItems

    const filteredItems = removeDraftEmptyItems(mutableTtsItems)

    state.ttsMutableItems.data = filteredItems
  },
  setTtsGenerationStatus: (
    state: ITtsState,
    action: PayloadAction<TTtsGenerationStatus | null>
  ) => {
    state.ttsGenerationStatus = action.payload
  },
  setNewGeneratedItemsInitialIdAndStatus: (
    state: ITtsState,
    action: PayloadAction<INewGeneratedItemsInitialIdAndStatus[]>
  ) => {
    state.newGeneratedItemsInitialIdAndStatus = action.payload
  },
  setIsTtsMismatchPopupVisible: (
    state: ITtsState,
    action: PayloadAction<boolean>
  ) => {
    state.isTtsMismatchPopupVisible = action.payload
  }
}

export default reducers
